import React, { useState, useEffect, useCallback } from 'react';
import { 
  Camera, 
  Video, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw,
  Loader2,
  Wifi,
  WifiOff
} from 'lucide-react';

/**
 * ThetaCameraSelector - Detects and selects RICOH THETA cameras
 * Supports: THETA X, Z1, SC2 via USB UVC mode
 */
const ThetaCameraSelector = ({ 
  onCameraSelected, 
  onStreamReady,
  autoStart = false
}) => {
  const [cameras, setCameras] = useState([]);
  const [thetaCameras, setThetaCameras] = useState([]);
  const [standardCameras, setStandardCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [stream, setStream] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);

  // THETA camera identification patterns
  const THETA_PATTERNS = [
    'RICOH THETA',
    'THETA UVC',
    'THETA X',
    'THETA Z1',
    'THETA SC2',
    'THETA S',
    'THETA V'
  ];

  // Check if a device is a THETA camera
  const isThetaCamera = (device) => {
    const label = device.label.toUpperCase();
    return THETA_PATTERNS.some(pattern => label.includes(pattern.toUpperCase()));
  };

  // Get THETA model from label
  const getThetaModel = (label) => {
    const upperLabel = label.toUpperCase();
    if (upperLabel.includes('THETA X')) return 'THETA X';
    if (upperLabel.includes('Z1')) return 'THETA Z1';
    if (upperLabel.includes('SC2')) return 'THETA SC2';
    if (upperLabel.includes('THETA V')) return 'THETA V';
    if (upperLabel.includes('THETA S')) return 'THETA S';
    return 'RICOH THETA';
  };

  // Request camera permissions and enumerate devices
  const detectCameras = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, request permission to access cameras
      const tempStream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: false 
      });
      
      // Stop the temporary stream immediately
      tempStream.getTracks().forEach(track => track.stop());
      setHasPermission(true);

      // Now enumerate all video devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      setCameras(videoDevices);

      // Separate THETA cameras from standard cameras
      const theta = videoDevices.filter(isThetaCamera);
      const standard = videoDevices.filter(d => !isThetaCamera(d));

      setThetaCameras(theta);
      setStandardCameras(standard);

      // Auto-select first THETA if available and autoStart is true
      if (autoStart && theta.length > 0) {
        selectCamera(theta[0], true);
      }

    } catch (err) {
      console.error('Camera detection error:', err);
      setHasPermission(false);
      
      if (err.name === 'NotAllowedError') {
        setError('Permission refusée. Veuillez autoriser l\'accès à la caméra.');
      } else if (err.name === 'NotFoundError') {
        setError('Aucune caméra détectée.');
      } else {
        setError(`Erreur de détection: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [autoStart]);

  // Select and start a camera
  const selectCamera = async (device, isTheta = false) => {
    setIsLoading(true);
    setError(null);

    try {
      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
        // Wait a bit for the camera to be released
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Configure constraints based on camera type
      const constraints = {
        video: {
          deviceId: { exact: device.deviceId },
          // THETA cameras support up to 4K equirectangular
          ...(isTheta ? {
            width: { ideal: 3840 },
            height: { ideal: 1920 },
            frameRate: { ideal: 30 }
          } : {
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          })
        },
        audio: false
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setStream(newStream);
      setSelectedCamera({ ...device, isTheta, model: isTheta ? getThetaModel(device.label) : 'Standard' });
      
      onCameraSelected?.({ device, isTheta, model: isTheta ? getThetaModel(device.label) : 'Standard' });
      onStreamReady?.(newStream, isTheta);

    } catch (err) {
      console.error('Camera selection error:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Impossible de démarrer la caméra';
      
      if (err.message?.includes('allocate') || err.name === 'NotReadableError') {
        errorMessage = 'La caméra est déjà utilisée par une autre application. Fermez les autres onglets ou applications qui utilisent la caméra, puis cliquez sur "Actualiser".';
      } else if (err.name === 'NotAllowedError') {
        errorMessage = 'Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres du navigateur.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Caméra non trouvée. Vérifiez qu\'elle est bien connectée.';
      } else if (err.name === 'OverconstrainedError') {
        // Try with lower resolution
        try {
          const fallbackConstraints = {
            video: { deviceId: { exact: device.deviceId } },
            audio: false
          };
          const fallbackStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          setStream(fallbackStream);
          setSelectedCamera({ ...device, isTheta, model: isTheta ? getThetaModel(device.label) : 'Standard' });
          onCameraSelected?.({ device, isTheta, model: isTheta ? getThetaModel(device.label) : 'Standard' });
          onStreamReady?.(fallbackStream, isTheta);
          setIsLoading(false);
          return; // Success with fallback
        } catch (fallbackErr) {
          errorMessage = 'Résolution non supportée par cette caméra.';
        }
      } else {
        errorMessage = `Erreur: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setSelectedCamera(null);
      onStreamReady?.(null, false);
    }
  };

  // Initial detection on mount
  useEffect(() => {
    detectCameras();

    // Listen for device changes
    const handleDeviceChange = () => {
      detectCameras();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      stopCamera();
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <div className="space-y-4" data-testid="theta-camera-selector">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="text-[#00D3A8]" size={20} />
          <span className="font-medium text-gray-900">Sélection de caméra</span>
        </div>
        <button
          onClick={detectCameras}
          disabled={isLoading}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#00D3A8] transition-colors"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  detectCameras();
                }}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-800 underline"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !error && (
        <div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg">
          <Loader2 size={24} className="animate-spin text-[#00D3A8]" />
          <span className="ml-2 text-gray-600">Détection des caméras...</span>
        </div>
      )}

      {/* Camera lists */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {/* THETA Cameras section */}
          {thetaCameras.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#00D3A8] rounded-full"></span>
                Caméras RICOH THETA (360°)
              </h4>
              <div className="space-y-2">
                {thetaCameras.map((camera) => (
                  <button
                    key={camera.deviceId}
                    onClick={() => selectCamera(camera, true)}
                    disabled={selectedCamera?.deviceId === camera.deviceId}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      selectedCamera?.deviceId === camera.deviceId
                        ? 'border-[#00D3A8] bg-[#00D3A8]/5'
                        : 'border-gray-200 hover:border-[#00D3A8]/50 bg-white'
                    }`}
                    data-testid={`camera-theta-${camera.deviceId}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        selectedCamera?.deviceId === camera.deviceId
                          ? 'bg-[#00D3A8] text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Video size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{getThetaModel(camera.label)}</p>
                        <p className="text-xs text-gray-500">{camera.label}</p>
                      </div>
                    </div>
                    {selectedCamera?.deviceId === camera.deviceId && (
                      <CheckCircle className="text-[#00D3A8]" size={20} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Standard Cameras section */}
          {standardCameras.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                Caméras standard
              </h4>
              <div className="space-y-2">
                {standardCameras.map((camera) => (
                  <button
                    key={camera.deviceId}
                    onClick={() => selectCamera(camera, false)}
                    disabled={selectedCamera?.deviceId === camera.deviceId}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      selectedCamera?.deviceId === camera.deviceId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                    data-testid={`camera-standard-${camera.deviceId}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        selectedCamera?.deviceId === camera.deviceId
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Camera size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">
                          {camera.label || 'Caméra sans nom'}
                        </p>
                        <p className="text-xs text-gray-500">Caméra standard</p>
                      </div>
                    </div>
                    {selectedCamera?.deviceId === camera.deviceId && (
                      <CheckCircle className="text-blue-500" size={20} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No cameras detected */}
          {cameras.length === 0 && hasPermission && (
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <WifiOff size={40} className="mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600">Aucune caméra détectée</p>
              <p className="text-sm text-gray-500 mt-1">
                Connectez une caméra RICOH THETA via USB
              </p>
            </div>
          )}

          {/* Selected camera info */}
          {selectedCamera && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wifi className="text-green-500" size={16} />
                  <span className="text-sm text-gray-700">
                    <strong>{selectedCamera.model}</strong> connectée
                  </span>
                </div>
                <button
                  onClick={stopCamera}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Déconnecter
                </button>
              </div>
              {selectedCamera.isTheta && (
                <p className="text-xs text-gray-500 mt-1">
                  Mode 360° actif - Résolution équirectangulaire 4K
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Permission request */}
      {hasPermission === false && (
        <div className="text-center p-6 bg-amber-50 rounded-lg">
          <AlertCircle size={40} className="mx-auto mb-3 text-amber-500" />
          <p className="text-amber-800 font-medium">Permission requise</p>
          <p className="text-sm text-amber-700 mt-1">
            Cliquez sur "Actualiser" et autorisez l'accès à la caméra
          </p>
          <button
            onClick={detectCameras}
            className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Autoriser l'accès
          </button>
        </div>
      )}
    </div>
  );
};

export default ThetaCameraSelector;
